import React from "react";
import { clsx } from "clsx";

import Headline from "~components/shared/headline/Headline";
import { type MediaConfig, AuthorImageMedia } from "~components/shared/media";
import PhoenixByline from "~components/shared/phoenix-byline/PhoenixByline";
import { HEADLINE_SIZE } from "~types/lineup";
import type { CuratedItem } from "~types/types";

import styles from "./ItemsWithHeadshots.module.css";

const MEDIA_CONFIG: MediaConfig = {
    useImageLink: false,
    className: styles.authorHeadshot,
    aspectRatio: 1,
    desktopMaxWidth: 72,
    mobileMaxWidth: 54,
};

const MAX_ITEMS = 3;

type HeadshotItemProps = {
    item: CuratedItem;
    itemIndex: number;
};

function HeadshotItem({ item, itemIndex }: HeadshotItemProps) {
    const { headline, url, media } = item;
    const { authorImage } = media || {};

    const isValidForHeadshot = Boolean(authorImage);

    return (
        <div
            className={styles.item}
            data-component="headshot-item"
            data-testid="headshot-item"
        >
            <a data-link-index={itemIndex} href={url} className={styles.link}>
                <div className={styles.info}>
                    <Headline
                        className={styles.headline}
                        isPhoenix
                        size={HEADLINE_SIZE.EXTRA_SMALL}
                        text={headline}
                    />
                    <PhoenixByline story={item} forceShowByline />
                </div>

                {isValidForHeadshot && (
                    <AuthorImageMedia
                        authorImage={authorImage}
                        config={MEDIA_CONFIG}
                    />
                )}
            </a>
        </div>
    );
}

type ItemsWithHeadshotsProps = {
    items?: CuratedItem[];
    isOpinion?: boolean;
};

export default function ItemsWithHeadshots({
    isOpinion = false,
    items,
}: ItemsWithHeadshotsProps) {
    if (!items?.length) return null;

    return (
        <div
            className={clsx([
                styles.ItemsWithHeadshots,
                { [styles.isOpinion]: isOpinion },
            ])}
            data-component="items-with-headshots"
        >
            {items.slice(0, MAX_ITEMS).map((item, index) => {
                return (
                    <HeadshotItem item={item} itemIndex={index} key={index} />
                );
            })}
        </div>
    );
}
